/**
 * The renderer. Runs in the render worker on an OffscreenCanvas, or on the main thread with a
 * regular canvas where WebGL-in-worker is unavailable. Same code in both places.
 *
 * Per frame (docs/02 "Per-frame data flow"):
 *  1. Work out which analysis time matches what the viewer will hear when this frame is shown.
 *  2. Pick that AudioFrame from the history; evaluate the (predicted) transport at that time.
 *  3. Smooth features, fill modulation sources, upload spectrum/waveform/spectrogram textures.
 *  4. Render the live scene (and the incoming one during a transition), mix, measure luminance.
 *  5. Present with blackout/flash-limit gain; fan out to output windows.
 *  6. Feed timings to the adaptive governor.
 */
import {
  F,
  NUM_BANDS,
  SPECTRUM_BINS,
  WAVEFORM_SAMPLES,
  createFrame,
  readFrameTime,
} from '../../shared/audioFrame';
import type {
  FromAnalysis,
  FromRender,
  OutputConfig,
  RenderInitMessage,
  RenderStats,
  ToRender,
} from '../../shared/messages';
import { hash01, lfoValue, SOURCE_INDEX } from '../../shared/modulation';
import { idleMood, type MoodState } from '../../shared/mood';
import type { Lfo } from '../../shared/modulation';
import type { Scene, TransitionSpec, TransitionType } from '../../shared/scene';
import { PREVIEW_MONITOR } from '../../shared/messages';
import { TRANSITION_TYPES } from '../../shared/scene';
import type { Settings } from '../../shared/settings';
import { absNowMs, analysisTimeFor, type LatencyInfo } from '../../shared/timing';
import {
  createPhase,
  idleTransport,
  nextBoundary,
  transportPhaseAt,
  type TransportState,
} from '../../shared/transport';
import { installPresetPack } from './butterchurn/ButterchurnViz';
import { FlashLimiter } from './FlashLimiter';
import {
  createFrameContext,
  PITCH_HISTORY_DT,
  UNIT_INPUT,
  UNIT_SPECTROGRAM,
  type FrameContext,
} from './frame';
import {
  FULLSCREEN_VS,
  TargetPool,
  bindTex,
  buildProgram,
  createDataTexture,
  createRenderTarget,
  deleteRenderTarget,
  uniform,
  unbindUnits,
  type GL,
  type RenderTarget,
} from './gl/gl';
import { Governor } from './Governor';
import { LumaProbe } from './LumaProbe';
import { MorphParticles } from './MorphParticles';
import { SourceRegistry } from './ModEvaluator';
import { SceneRenderer, type BuiltinPrograms, type SceneDeps } from './SceneRenderer';
import * as S from './shaders';
import { FrameHistory, TimeMap } from './timebase';
import { ProgramCache } from './viz/instances';
import { VizRegistry } from './viz/registry';

export interface RendererHost {
  post(msg: FromRender, transfer?: Transferable[]): void;
  requestFrame(cb: (timestamp: number) => void): void;
}

/** Hook for kinds that need GL access (ISF, Butterchurn) to register their factories. */
export type KindInstaller = (
  registry: VizRegistry,
  deps: { gl: GL; cache: ProgramCache; post: RendererHost['post'] },
) => void;

export class WebGLUnavailableError extends Error {}

const STATS_INTERVAL_MS = 500;
const SPECTROGRAM_ROWS = 64;

// Source indices (fixed order from MOD_SOURCES).
const SRC = (id: string) => SOURCE_INDEX.get(id)!;
/** A scene on its way to the screens (see Renderer.takes). */
interface Take {
  id: number;
  scene: SceneRenderer;
  target: RenderTarget | null;
  type: TransitionType;
  position: number;
  /** Running on its own from `from`; null while held by the T-bar. */
  auto: { startMs: number; from: number; durationMs: number } | null;
}

const I = {
  melodyPitch: SRC('melody.pitch'),
  melodyNote: SRC('melody.note'),
  melodyConf: SRC('melody.confidence'),
  level: SRC('audio.level'),
  bass: SRC('audio.bass'),
  mid: SRC('audio.mid'),
  treble: SRC('audio.treble'),
  band0: SRC('audio.band0'),
  onset: SRC('audio.onset'),
  brightness: SRC('audio.brightness'),
  pulse: SRC('beat.pulse'),
  phase: SRC('beat.phase'),
  sine: SRC('beat.sine'),
  half: SRC('beat.half'),
  quarter: SRC('beat.quarter'),
  bar: SRC('beat.bar'),
  phrase: SRC('beat.phrase'),
  anticipation: SRC('beat.anticipation'),
  random: SRC('beat.random'),
  lfo1: SRC('lfo.1'),
  energy: SRC('mood.energy'),
  arousal: SRC('mood.arousal'),
  valence: SRC('mood.valence'),
  buildup: SRC('mood.buildup'),
  drop: SRC('mood.drop'),
  slow: SRC('time.slow'),
};

interface Output {
  canvas: OffscreenCanvas | HTMLCanvasElement;
  ctx: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D;
  config: OutputConfig;
}

interface PendingTransition {
  scene: SceneRenderer;
  spec: TransitionSpec;
  /** Analysis time the transition starts (null until computed on the next frame). */
  start: number | null;
  duration: number;
}

export class Renderer {
  private readonly gl: GL;
  private readonly fc: FrameContext;
  private readonly pool: TargetPool;
  private readonly programs: BuiltinPrograms;
  private readonly cache: ProgramCache;
  readonly registry = new VizRegistry();
  private readonly sources = new SourceRegistry();
  private readonly deps: SceneDeps;
  private spectrumTex!: WebGLTexture;
  private waveformTex!: WebGLTexture;
  private spectrogramTex!: WebGLTexture;
  private final: RenderTarget | null = null;
  private black!: RenderTarget;
  private morph!: MorphParticles;
  private luma!: LumaProbe;
  private readonly limiter = new FlashLimiter();

  private live: SceneRenderer;
  private next: PendingTransition | null = null;
  /**
   * Preview mode: the scene being prepared, shown in the main window while the outputs (and a
   * corner monitor) show live. A take mixes live towards it, then it becomes live.
   */
  private preview: SceneRenderer | null = null;
  private previewTarget: RenderTarget | null = null;
  /**
   * Takes on their way to the screens, oldest first. Each is a scene locked when its fade (or
   * T-bar move) started, so edits after that build the next preview. Several can run at once;
   * they layer over live in order.
   */
  private takes: Take[] = [];
  private previewMix = 0;
  /** While recording: a canvas the live picture is copied into and sent to the page. */
  private recordCanvas: OffscreenCanvas | null = null;
  private recordCtx: OffscreenCanvasRenderingContext2D | null = null;
  private settings: Settings;
  private latency: LatencyInfo;
  private transport: TransportState = idleTransport();
  private mood: MoodState = idleMood();
  private readonly history = new FrameHistory();
  private readonly timeMap = new TimeMap();
  private readonly governor: Governor;
  private readonly silent = createFrame();
  private readonly phase = createPhase();
  private readonly outputs = new Map<string, Output>();

  private width = 1;
  private height = 1;
  private visible = true;
  /** Drives frames while the control window is hidden but output windows still need pictures. */
  private hiddenTimer: ReturnType<typeof setInterval> | null = null;
  private lost = false;
  private disposed = false;
  private blackout = false;
  private freeze = false;
  private blackoutGain = 1;
  private readonly startMs = performance.now();
  private lastFrameMs = 0;
  private lastDropSeen = -Infinity;
  private stalls = 0;
  private pitchClock = 0;
  private lastWatchCheck = 0;
  private dropEnv = 0;

  // Smoothed values
  private readonly spectrum = new Float32Array(SPECTRUM_BINS);
  private pulse = 0;
  private atk = 1;
  private rel = 1;

  // Stats
  private framesSinceStats = 0;
  private totalFrames = 0;
  private cpuAccum = 0;
  private lastStats = 0;
  private predictMs = 0;
  private flashGain = 1;

  constructor(
    private readonly canvas: OffscreenCanvas | HTMLCanvasElement,
    init: Omit<RenderInitMessage, 'canvas'>,
    private readonly host: RendererHost,
    private readonly mode: 'worker' | 'main',
    installKinds: KindInstaller[] = [],
  ) {
    const gl = canvas.getContext('webgl2', {
      alpha: false,
      antialias: false,
      depth: false,
      stencil: false,
      preserveDrawingBuffer: false,
      powerPreference: 'high-performance',
      desynchronized: true,
    }) as GL | null;
    if (!gl) throw new WebGLUnavailableError('WebGL2 is not available');
    this.gl = gl;
    const parallel = gl.getExtension('KHR_parallel_shader_compile') as {
      COMPLETION_STATUS_KHR: number;
    } | null;
    this.cache = new ProgramCache(gl, parallel, (key, message) =>
      this.host.post({
        type: 'error',
        code: 'shader-error',
        message: `${key.replace(/^\w+:/, '')}: ${message}`,
      }),
    );
    this.pool = new TargetPool(gl);
    this.programs = this.buildPrograms();
    this.createTextures();
    const vao = gl.createVertexArray();
    if (!vao) throw new WebGLUnavailableError('createVertexArray failed');
    this.fc = createFrameContext(gl, vao, {
      spectrum: this.spectrumTex,
      waveform: this.waveformTex,
      spectrogram: this.spectrogramTex,
    });
    this.deps = {
      gl,
      pool: this.pool,
      programs: this.programs,
      cache: this.cache,
      registry: this.registry,
      sources: this.sources,
      black: this.black,
      onLayerError: (name, message) =>
        this.host.post({ type: 'error', code: 'shader-error', message: `${name}: ${message}` }),
    };
    for (const install of installKinds)
      install(this.registry, { gl, cache: this.cache, post: (m, t) => this.host.post(m, t) });
    this.registry.setCustom(init.customViz);

    this.settings = init.settings;
    this.latency = init.latency;
    this.governor = new Governor(this.governorConfig());
    this.live = new SceneRenderer(this.deps, init.scene, init.settings.intensity);

    init.framePort.onmessage = (e: MessageEvent<Float32Array>) => {
      this.history.push(e.data);
      this.timeMap.add(readFrameTime(e.data), absNowMs());
    };
    init.transportPort.onmessage = (e: MessageEvent<FromAnalysis>) => {
      if (e.data.type === 'transport') {
        this.transport = e.data.transport;
        this.mood = e.data.mood;
      }
    };

    canvas.addEventListener('webglcontextlost', (e) => {
      e.preventDefault();
      this.lost = true;
      this.host.post({
        type: 'error',
        code: 'context-lost',
        message: 'The GPU context was lost; recovering…',
      });
    });
    canvas.addEventListener('webglcontextrestored', () => this.restore());

    this.resize(init.width, init.height, init.dpr);
    this.host.post({ type: 'ready', renderer: this.rendererName() });
    this.host.requestFrame(this.onAnimationFrame);
  }

  handle(msg: ToRender): void {
    switch (msg.type) {
      case 'resize':
        this.resize(msg.width, msg.height, msg.dpr);
        break;
      case 'scene':
        this.setScene(msg.scene, msg.transition);
        break;
      case 'settings':
        this.settings = msg.settings;
        this.governor.configure(this.governorConfig());
        break;
      case 'latency':
        this.latency = msg.latency;
        break;
      case 'visibility':
        this.visible = msg.visible;
        this.updateHiddenDriver();
        break;
      case 'customViz':
        this.registry.setCustom(msg.defs);
        for (const d of msg.defs) this.cache.forget(`native:${d.id}`);
        this.live.update(this.live.scene, this.settings.intensity);
        break;
      case 'midi':
        this.sources.setDynamic(msg.source, msg.value);
        break;
      case 'master':
        this.blackout = msg.blackout;
        this.freeze = msg.freeze;
        break;
      case 'thumbnail':
        this.thumbnail(msg.id, msg.width, msg.height);
        break;
      case 'addOutput':
        this.addOutput(msg.id, msg.canvas, msg.width, msg.height, msg.config);
        this.updateHiddenDriver();
        break;
      case 'updateOutput': {
        const o = this.outputs.get(msg.id);
        if (!o) break;
        if (msg.width) o.canvas.width = Math.max(1, Math.round(msg.width));
        if (msg.height) o.canvas.height = Math.max(1, Math.round(msg.height));
        if (msg.config) o.config = { ...o.config, ...msg.config };
        break;
      }
      case 'removeOutput':
        this.outputs.delete(msg.id);
        this.updateHiddenDriver();
        break;
      case 'recordLive':
        if (msg.width > 0 && msg.height > 0 && typeof OffscreenCanvas !== 'undefined') {
          this.recordCanvas = new OffscreenCanvas(msg.width, msg.height);
          this.recordCtx = this.recordCanvas.getContext('2d', { alpha: false });
        } else {
          this.recordCanvas = null;
          this.recordCtx = null;
        }
        break;
      case 'preview':
        if (!msg.scene) {
          // Leaving preview mode: the newest take goes live at once.
          if (this.takes.length) this.completeTake(this.takes.length - 1);
          this.preview?.dispose();
          this.preview = null;
          if (this.previewTarget) deleteRenderTarget(this.gl, this.previewTarget);
          this.previewTarget = null;
        } else if (this.preview) this.preview.update(msg.scene, this.settings.intensity);
        else this.preview = new SceneRenderer(this.deps, msg.scene, this.settings.intensity);
        break;
      case 'take.start': {
        const p = this.preview;
        if (!p) break;
        // Lock the preview's renderer (with its trails state) as the take; a fresh renderer of
        // the same scene carries on as the preview.
        this.preview = new SceneRenderer(this.deps, p.scene, this.settings.intensity);
        const position = Math.max(0, Math.min(1, msg.position));
        this.takes.push({
          id: msg.id,
          scene: p,
          target: null,
          type: this.effectiveTransition({
            type: msg.transition,
            duration: msg.durationMs,
            unit: 'ms',
            quantize: 'immediate',
          }).type,
          position,
          auto: msg.manual
            ? null
            : {
                startMs: performance.now(),
                from: position,
                durationMs: Math.max(1, msg.durationMs),
              },
        });
        break;
      }
      case 'take.set': {
        const t = this.takes.find((x) => x.id === msg.id);
        if (!t) break;
        t.auto = null;
        t.position = Math.max(0, Math.min(1, msg.position));
        break;
      }
      case 'take.resume': {
        const t = this.takes.find((x) => x.id === msg.id);
        if (t)
          t.auto = {
            startMs: performance.now(),
            from: t.position,
            durationMs: Math.max(1, msg.durationMs),
          };
        break;
      }
      case 'take.complete': {
        const i = this.takes.findIndex((x) => x.id === msg.id);
        if (i >= 0) this.completeTake(i);
        break;
      }
      case 'take.cancel': {
        const i = this.takes.findIndex((x) => x.id === msg.id);
        if (i >= 0) this.disposeTake(this.takes.splice(i, 1)[0]!);
        break;
      }
      case 'take.cut': {
        // Cut: the preview as it is now goes live over everything that's fading.
        const p = this.preview;
        if (!p) break;
        this.preview = new SceneRenderer(this.deps, p.scene, this.settings.intensity);
        this.takes.push({ id: -1, scene: p, target: null, type: 'cut', position: 1, auto: null });
        this.completeTake(this.takes.length - 1);
        break;
      }
      case 'milkdropPack': {
        let names: string[] = [];
        try {
          names = installPresetPack(msg.code);
        } catch (err) {
          this.host.post({
            type: 'error',
            code: 'shader-error',
            message: `MilkDrop pack failed to load: ${String(err)}`,
          });
        }
        this.host.post({ type: 'milkdropPresets', names });
        break;
      }
      case 'init':
        break;
    }
  }

  dispose(): void {
    this.disposed = true;
    this.updateHiddenDriver();
  }

  // ---- setup

  private buildPrograms(): BuiltinPrograms {
    const gl = this.gl;
    return {
      blit: buildProgram(gl, FULLSCREEN_VS, S.BLIT_FS),
      composite: buildProgram(gl, FULLSCREEN_VS, S.COMPOSITE_FS),
      mirror: buildProgram(gl, FULLSCREEN_VS, S.MIRROR_FS),
      feedback: buildProgram(gl, FULLSCREEN_VS, S.FEEDBACK_FS),
      bright: buildProgram(gl, FULLSCREEN_VS, S.BRIGHT_FS),
      blur: buildProgram(gl, FULLSCREEN_VS, S.BLUR_FS),
      uber: buildProgram(gl, FULLSCREEN_VS, S.UBER_FS),
      transition: buildProgram(gl, FULLSCREEN_VS, S.TRANSITION_FS),
      view: buildProgram(gl, FULLSCREEN_VS, S.VIEW_FS),
    };
  }

  private createTextures(): void {
    const gl = this.gl;
    this.spectrumTex = createDataTexture(gl, SPECTRUM_BINS, gl.R16F);
    this.waveformTex = createDataTexture(gl, WAVEFORM_SAMPLES, gl.R16F);
    this.spectrogramTex = createDataTexture(gl, SPECTRUM_BINS, gl.R16F, SPECTROGRAM_ROWS);
    this.black = createRenderTarget(gl, 1, 1);
    this.luma = new LumaProbe(gl, this.programs.blit);
    this.morph = new MorphParticles(gl, this.pool, this.programs.blit, () => this.fc.vao);
  }

  /** Rebuild everything after a GPU context loss. */
  private restore(): void {
    this.cache.clear();
    this.pool.clear();
    this.final = null;
    Object.assign(this.programs, this.buildPrograms());
    this.createTextures();
    const vao = this.gl.createVertexArray();
    if (vao) this.fc.vao = vao;
    this.fc.spectrumTex = this.spectrumTex;
    this.fc.waveformTex = this.waveformTex;
    this.fc.spectrogramTex = this.spectrogramTex;
    this.deps.black = this.black;
    const scene = this.next?.scene.scene ?? this.live.scene;
    this.next = null;
    this.live = new SceneRenderer(this.deps, scene, this.settings.intensity);
    this.lost = false;
  }

  private rendererName(): string {
    const gl = this.gl;
    const dbg = gl.getExtension('WEBGL_debug_renderer_info');
    return String(
      dbg ? gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER),
    );
  }

  private governorConfig() {
    const s = this.settings;
    return {
      adaptive: s.adaptiveQuality,
      targetFps: s.targetFps,
      scaleMin: s.scaleMin,
      scaleMax: s.scaleMax,
    };
  }

  private resize(cssWidth: number, cssHeight: number, dpr: number): void {
    this.width = Math.max(1, Math.round(cssWidth * dpr));
    this.height = Math.max(1, Math.round(cssHeight * dpr));
    this.canvas.width = this.width;
    this.canvas.height = this.height;
  }

  // ---- scenes and transitions

  private setScene(scene: Scene, transition?: TransitionSpec): void {
    if (!transition) {
      // An edit: apply to whatever will be on screen (the incoming scene, if transitioning).
      if (this.next) this.next.scene.update(scene, this.settings.intensity);
      else this.live.update(scene, this.settings.intensity);
      return;
    }
    const spec = this.effectiveTransition(transition);
    if (this.next) this.next.scene.dispose();
    this.next = {
      scene: new SceneRenderer(this.deps, scene, this.settings.intensity),
      spec,
      start: null,
      duration: 0,
    };
  }

  /** Take i is fully on: it becomes live, and the older takes it covers are dropped. */
  private completeTake(i: number): void {
    const t = this.takes[i];
    if (!t) return;
    if (this.next) {
      this.next.scene.dispose();
      this.next = null;
    }
    for (let k = 0; k < i; k++) this.disposeTake(this.takes[k]!);
    this.live.dispose();
    this.live = t.scene;
    if (t.target) deleteRenderTarget(this.gl, t.target);
    this.takes.splice(0, i + 1);
  }

  private disposeTake(t: Take): void {
    t.scene.dispose();
    if (t.target) deleteRenderTarget(this.gl, t.target);
  }

  private effectiveTransition(t: TransitionSpec): TransitionSpec {
    if (!TRANSITION_TYPES.includes(t.type)) return { ...t, type: 'crossfade' };
    // Reduced motion: no zooms/wipes; cuts become short fades (a hard cut is a flash).
    if (this.settings.reducedMotion && t.type !== 'crossfade') return { ...t, type: 'crossfade' };
    return t;
  }

  /** Decide when a pending transition starts and how long it lasts (needs the transport). */
  private scheduleTransition(target: number): void {
    const n = this.next;
    if (!n || n.start !== null) return;
    const tr = this.transport;
    const period = tr.locked ? tr.period : 0.5;
    const phraseBars = this.settings.phraseBars;
    let start = target;
    if (tr.locked) {
      switch (n.spec.quantize) {
        case 'beat':
        case 'bar':
        case 'phrase':
          start = nextBoundary(tr, target, n.spec.quantize, phraseBars);
          break;
        case 'drop': {
          const d = this.mood.nextDropTime;
          start =
            d !== null && d > target && d < target + 64 * period
              ? d
              : nextBoundary(tr, target, 'bar', phraseBars);
          break;
        }
        case 'immediate':
          break;
      }
    }
    n.start = start;
    const raw = n.spec.unit === 'beats' ? n.spec.duration * period : n.spec.duration / 1000;
    n.duration =
      n.spec.type === 'cut'
        ? this.settings.safeMode || this.settings.reducedMotion
          ? 0.25
          : 0
        : raw;
  }

  // ---- frame loop

  private readonly onAnimationFrame = (timestamp: number) => {
    if (this.disposed) return;
    this.host.requestFrame(this.onAnimationFrame);
    if (!this.visible) return; // the hidden driver takes over when outputs are open
    this.tick(timestamp);
  };

  /**
   * Browsers stop requestAnimationFrame for hidden documents (a minimised control window),
   * which would freeze every output window on the projectors. While hidden with outputs open,
   * frames come from a timer instead (unthrottled in the render worker; ~1 Hz on the main-thread
   * fallback, which is the best a hidden page can do).
   */
  private updateHiddenDriver(): void {
    const want = !this.disposed && !this.visible && this.outputs.size > 0;
    if (want && !this.hiddenTimer) {
      this.hiddenTimer = setInterval(() => this.tick(performance.now()), 1000 / 60);
    } else if (!want && this.hiddenTimer) {
      clearInterval(this.hiddenTimer);
      this.hiddenTimer = null;
    }
  }

  private readonly tick = (timestamp: number) => {
    if (this.lost) return;
    if (!this.governor.tick(timestamp)) return;
    const t0 = performance.now();
    this.renderFrame(t0);
    const cpu = performance.now() - t0;
    this.governor.frameDone(timestamp, cpu);
    this.pool.endFrame();
    this.morph.endFrame();
    this.recordStats(t0, cpu);
  };

  private renderFrame(nowMs: number): void {
    const gl = this.gl;
    const fc = this.fc;
    const s = this.settings;
    const rawDt = this.lastFrameMs ? (nowMs - this.lastFrameMs) / 1000 : 1 / 60;
    const dt = Math.min(0.1, rawDt);
    this.lastFrameMs = nowMs;
    this.watchdog(rawDt, nowMs);

    // 1–2. Audio matching what will be heard when this frame reaches the screen.
    let frame = this.silent;
    let target = 0;
    if (this.timeMap.ready) {
      const displayGraphTime = this.timeMap.graphTimeAt(absNowMs() + s.visualLatencyMs);
      target = analysisTimeFor(displayGraphTime, this.latency, s.syncOffsetMs / 1000);
      frame = this.history.select(target) ?? this.silent;
      this.predictMs = (target - this.history.newestTime) * 1000;
    }

    const w = Math.max(1, Math.round(this.width * this.governor.scale));
    const h = Math.max(1, Math.round(this.height * this.governor.scale));
    if (!this.final || this.final.width !== w || this.final.height !== h) {
      if (this.final) deleteRenderTarget(gl, this.final);
      this.final = createRenderTarget(gl, w, h);
    }

    if (!this.freeze) {
      this.updateFrameContext(frame, target, dt, nowMs, w, h);
      this.uploadTextures(frame);
      this.fillSources(target);
      this.scheduleTransition(target);
      this.drawScenes(target);
      this.drawPreview(w, h, nowMs);
    }
    // Live picture: the scene, or (during a take) the mix towards the preview.
    const liveTex = this.mixLive();

    // 5. Present: flash limiting (measured luminance), blackout fade, then output windows.
    const measured = this.luma.sample(liveTex.texture);
    const limit = this.limiter.update(measured, dt, s.safeMode);
    this.flashGain +=
      (limit - this.flashGain) * (limit < this.flashGain ? 1 : 1 - Math.exp(-dt / 0.15));
    const fadeTarget = this.blackout ? 0 : 1;
    this.blackoutGain += (fadeTarget - this.blackoutGain) * (1 - Math.exp(-dt / 0.08));
    const gain = this.flashGain * this.blackoutGain;

    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, this.width, this.height);
    gl.bindVertexArray(fc.vao);
    const p = this.programs.blit;
    gl.useProgram(p.program);
    bindTex(gl, p, 'uTex', UNIT_INPUT, liveTex.texture);
    gl.uniform1f(uniform(gl, p, 'uGain'), gain);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    unbindUnits(gl, UNIT_INPUT, UNIT_INPUT);
    // Outputs (and the recorder) copy the canvas now, while it holds the live picture…
    this.presentOutputs();
    this.sendRecordingFrame();
    // …then, in preview mode, the main window shows the preview with live in a corner monitor.
    if (this.preview && this.previewTarget) this.presentPreview(liveTex, gain);
    if (liveTex !== this.final) this.pool.release(liveTex);
    // A timed take that has run its course is live now (a manual one waits for its release).
    for (let i = this.takes.length - 1; i >= 0; i--) {
      const t = this.takes[i]!;
      if (t.auto && t.position >= 1) {
        this.completeTake(i);
        // Report it now, not on the next frame (which can be a while on a slow GPU).
        this.previewMix = this.takes.at(-1)?.position ?? 0;
        break;
      }
    }
  }

  /** Recording: hand the live picture to the page (works in every browser, unlike capturing a
   * worker-drawn 2D canvas). */
  private sendRecordingFrame(): void {
    const c = this.recordCanvas;
    const ctx = this.recordCtx;
    if (!c || !ctx) return;
    ctx.drawImage(this.canvas as CanvasImageSource, 0, 0, c.width, c.height);
    const bitmap = c.transferToImageBitmap();
    this.host.post({ type: 'liveFrame', bitmap }, [bitmap]);
  }

  private drawPreview(w: number, h: number, nowMs: number): void {
    const pv = this.preview;
    if (!pv) {
      this.previewMix = -1;
      return;
    }
    if (!this.previewTarget || this.previewTarget.width !== w || this.previewTarget.height !== h) {
      if (this.previewTarget) deleteRenderTarget(this.gl, this.previewTarget);
      this.previewTarget = createRenderTarget(this.gl, w, h);
    }
    this.writeLfos(pv.scene.lfos);
    pv.render(this.fc, this.sources.values, this.previewTarget);
    for (const t of this.takes) {
      if (t.auto)
        t.position = Math.min(1, t.auto.from + (nowMs - t.auto.startMs) / t.auto.durationMs);
      if (!t.target || t.target.width !== w || t.target.height !== h) {
        if (t.target) deleteRenderTarget(this.gl, t.target);
        t.target = createRenderTarget(this.gl, w, h);
      }
      this.writeLfos(t.scene.scene.lfos);
      t.scene.morphFrom =
        t.type === 'morph' && !this.settings.reducedMotion
          ? { scene: this.live, p: t.position }
          : null;
      t.scene.render(this.fc, this.sources.values, t.target);
      t.scene.morphFrom = null;
    }
    this.previewMix = this.takes.at(-1)?.position ?? 0;
  }

  /**
   * How a morph is drawn: layers that keep their visualization glide their settings (see
   * SceneRenderer.morphFrom), so when every layer does, a crossfade over that is the morph; when
   * the looks differ, points travel between their shapes (MorphParticles) over a dissolve.
   */
  private morphKind(to: SceneRenderer): 'glide' | 'particles' {
    const a = this.live.scene.layers;
    const b = to.scene.layers;
    return a.length === b.length && a.every((l, i) => l.vizId === b[i]!.vizId)
      ? 'glide'
      : 'particles';
  }

  /** The live picture: `final`, or a pool target holding the mix from live towards the preview. */
  private mixLive(): RenderTarget {
    const final = this.final!;
    if (!this.preview) return final;
    const gl = this.gl;
    const p = this.programs.transition;
    let base = final;
    // Each take layers over what's beneath it, oldest first.
    for (const t of this.takes) {
      if (!t.target || t.position <= 0) continue;
      // Reduced motion: takes crossfade, like scene changes do.
      let type = t.type === 'cut' || this.settings.reducedMotion ? 'crossfade' : t.type;
      const particles = type === 'morph' && this.morphKind(t.scene) === 'particles';
      if (type === 'morph' && !particles) type = 'crossfade';
      const out = this.pool.acquire(final.width, final.height);
      gl.bindFramebuffer(gl.FRAMEBUFFER, out.fbo);
      gl.viewport(0, 0, out.width, out.height);
      gl.bindVertexArray(this.fc.vao);
      gl.useProgram(p.program);
      bindTex(gl, p, 'uA', UNIT_INPUT, base.texture);
      bindTex(gl, p, 'uB', UNIT_INPUT + 1, t.target.texture);
      gl.uniform1f(uniform(gl, p, 'uP'), t.position);
      gl.uniform1i(uniform(gl, p, 'uType'), Math.max(1, TRANSITION_TYPES.indexOf(type)));
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      unbindUnits(gl, UNIT_INPUT, UNIT_INPUT + 1);
      if (particles) this.morph.draw(t, base, t.target, out, t.position);
      if (base !== final) this.pool.release(base);
      base = out;
    }
    return base;
  }

  /** Main window in preview mode: the preview full-screen, live in a framed corner monitor. */
  private presentPreview(liveTex: RenderTarget, liveGain: number): void {
    const gl = this.gl;
    const p = this.programs.blit;
    gl.useProgram(p.program);
    bindTex(gl, p, 'uTex', UNIT_INPUT, this.previewTarget!.texture);
    gl.uniform1f(uniform(gl, p, 'uGain'), this.flashGain);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    const m = PREVIEW_MONITOR;
    const pw = Math.round(this.width * m.width);
    const ph = Math.round((pw * this.height) / this.width);
    const x = Math.round(this.width * m.left);
    const y = this.height - ph - Math.round(this.height * m.top);
    const border = Math.max(2, Math.round(this.width / 600));
    gl.enable(gl.SCISSOR_TEST);
    gl.scissor(x - border, y - border, pw + 2 * border, ph + 2 * border);
    gl.clearColor(0.92, 0.12, 0.18, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.clearColor(0, 0, 0, 1);
    gl.disable(gl.SCISSOR_TEST);
    gl.viewport(x, y, pw, ph);
    bindTex(gl, p, 'uTex', UNIT_INPUT, liveTex.texture);
    gl.uniform1f(uniform(gl, p, 'uGain'), liveGain);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    gl.viewport(0, 0, this.width, this.height);
    unbindUnits(gl, UNIT_INPUT, UNIT_INPUT);
  }

  private updateFrameContext(
    frame: Float32Array,
    target: number,
    dt: number,
    nowMs: number,
    w: number,
    h: number,
  ): void {
    const fc = this.fc;
    const s = this.settings;
    const sm = s.reducedMotion ? Math.max(0.8, s.smoothing) : s.smoothing;
    this.atk = 1 - Math.exp(-dt / (0.008 + 0.062 * sm));
    this.rel = 1 - Math.exp(-dt / (0.08 + 0.47 * sm));
    for (let b = 0; b < NUM_BANDS; b++)
      fc.bands[b] = this.follow(fc.bands[b]!, frame[F.BANDS + b]!);
    for (let i = 0; i < SPECTRUM_BINS; i++)
      this.spectrum[i] = this.follow(this.spectrum[i]!, frame[F.SPECTRUM + i]!);
    fc.level = this.follow(fc.level, frame[F.RMS]!);
    fc.bass = this.follow(fc.bass, frame[F.BASS]!);
    fc.mid = this.follow(fc.mid, frame[F.MID]!);
    fc.treble = this.follow(fc.treble, frame[F.TREBLE]!);
    fc.centroid = this.follow(fc.centroid, frame[F.CENTROID]!);
    this.updatePitch(frame, dt);
    fc.onset = frame[F.ONSET]!;

    // Beat pulse: blend the predicted beat with reactive hits by tracker confidence.
    const tr = this.transport;
    const conf = tr.locked ? Math.min(1, tr.confidence * 1.2) : 0;
    let predicted = 0;
    if (tr.locked && this.timeMap.ready) {
      transportPhaseAt(tr, target, this.phase, s.phraseBars);
      predicted = Math.exp(-this.phase.sinceBeat / ((0.18 + 0.22 * sm) * tr.period));
    } else {
      this.phase.beatPhase =
        this.phase.barPhase =
        this.phase.phrasePhase =
        this.phase.beatInBar =
          0;
    }
    const lastHit = this.history.lastHitBefore(target, F.HIT);
    const reactive = Number.isFinite(lastHit)
      ? Math.exp(-(target - lastHit) / (0.08 + 0.2 * sm))
      : 0;
    let pulseRaw = conf * predicted + (1 - conf) * reactive * (1 - 0.5 * sm);
    if (s.safeMode) pulseRaw *= 0.6;
    const attackS = Math.max(s.safeMode ? 0.04 : 0, 0.03 * sm);
    const pulseAtk = attackS > 0 ? 1 - Math.exp(-dt / attackS) : 1;
    this.pulse +=
      (pulseRaw - this.pulse) *
      (pulseRaw > this.pulse ? pulseAtk : 1 - Math.exp(-dt / (0.08 + 0.15 * sm)));

    fc.frameData = frame;
    fc.width = w;
    fc.height = h;
    fc.time = (nowMs - this.startMs) / 1000;
    fc.dt = dt;
    fc.frame++;
    fc.beatPhase = this.phase.beatPhase;
    fc.barPhase = this.phase.barPhase;
    // Without a tempo lock, keep beat-stepped patterns moving at 120 BPM.
    fc.beats = this.transport.locked ? this.phase.beat + this.phase.beatPhase : fc.time * 2;
    fc.phrasePhase = this.phase.phrasePhase;
    fc.beatInBar = this.phase.beatInBar;
    fc.pulse = this.pulse;
    fc.bpm = tr.bpm;
    fc.confidence = conf;
    fc.energy = this.mood.energy;
    fc.buildup = this.mood.buildup;
    fc.beatReact = s.beatReact;
    fc.tier = this.governor.tier;
  }

  private uploadTextures(frame: Float32Array): void {
    const gl = this.gl;
    gl.activeTexture(gl.TEXTURE0 + UNIT_INPUT);
    gl.bindTexture(gl.TEXTURE_2D, this.spectrumTex);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, SPECTRUM_BINS, 1, gl.RED, gl.FLOAT, this.spectrum);
    gl.bindTexture(gl.TEXTURE_2D, this.waveformTex);
    gl.texSubImage2D(
      gl.TEXTURE_2D,
      0,
      0,
      0,
      WAVEFORM_SAMPLES,
      1,
      gl.RED,
      gl.FLOAT,
      frame,
      F.WAVEFORM,
    );
    gl.activeTexture(gl.TEXTURE0 + UNIT_SPECTROGRAM);
    gl.bindTexture(gl.TEXTURE_2D, this.spectrogramTex);
    this.fc.spectrogramRow = (this.fc.spectrogramRow + 1) % SPECTROGRAM_ROWS;
    gl.texSubImage2D(
      gl.TEXTURE_2D,
      0,
      0,
      this.fc.spectrogramRow,
      SPECTRUM_BINS,
      1,
      gl.RED,
      gl.FLOAT,
      this.spectrum,
    );
    gl.activeTexture(gl.TEXTURE0 + UNIT_INPUT);
    gl.bindTexture(gl.TEXTURE_2D, null);
  }

  /** Fill the modulation source values for this frame (LFOs are per scene; see drawScenes). */
  private fillSources(target: number): void {
    const v = this.sources.values;
    const fc = this.fc;
    v[I.level] = fc.level;
    v[I.bass] = fc.bass;
    v[I.mid] = fc.mid;
    v[I.treble] = fc.treble;
    for (let b = 0; b < NUM_BANDS; b++) v[I.band0 + b] = fc.bands[b]!;
    v[I.onset] = fc.onset;
    v[I.brightness] = fc.centroid;
    v[I.pulse] = fc.pulse;
    const bp = fc.beatPhase;
    v[I.phase] = bp;
    v[I.sine] = 0.5 - 0.5 * Math.cos(bp * 2 * Math.PI);
    v[I.half] = (bp * 2) % 1;
    v[I.quarter] = (bp * 4) % 1;
    v[I.bar] = fc.barPhase;
    v[I.phrase] = fc.phrasePhase;
    v[I.anticipation] = bp > 0.75 ? (bp - 0.75) * 4 : 0;
    v[I.random] = hash01(this.phase.beat);
    const m = this.mood;
    v[I.energy] = m.energy;
    v[I.arousal] = m.arousal;
    v[I.valence] = m.valence;
    v[I.buildup] = m.buildup;
    if (m.lastDropTime > this.lastDropSeen && m.lastDropTime <= target) {
      this.lastDropSeen = m.lastDropTime;
      this.dropEnv = 1;
    }
    this.dropEnv *= Math.exp(-fc.dt / 1.5);
    v[I.drop] = this.dropEnv;
    v[I.slow] = 0.5 - 0.5 * Math.cos(fc.time * 0.1);
    // Melody: pitch over A2…C7 as 0..1, the note within the octave, and how clear it is.
    v[I.melodyPitch] = fc.pitch > 0 ? Math.min(1, Math.max(0, (fc.pitch - 45) / 51)) : 0;
    v[I.melodyNote] = fc.pitch > 0 ? (((fc.pitch % 12) + 12) % 12) / 12 : 0;
    v[I.melodyConf] = fc.pitchConf;
  }

  private writeLfos(lfos: readonly Lfo[]): void {
    const v = this.sources.values;
    const beats = this.phase.beat + this.phase.beatPhase;
    for (let i = 0; i < lfos.length && i < 4; i++) {
      const l = lfos[i]!;
      const phase = l.sync ? beats / Math.max(0.01, l.rate) : this.fc.time * l.rate;
      v[I.lfo1 + i] = lfoValue(l.shape, phase + i * 1009, lfoRandom);
    }
  }

  private drawScenes(target: number): void {
    const gl = this.gl;
    const final = this.final!;
    const n = this.next;
    let progress = -1;
    if (n && n.start !== null && target >= n.start && n.scene.ready) {
      progress = n.duration > 0 ? (target - n.start) / n.duration : 1;
    }
    if (n && progress >= 1) {
      this.live.dispose();
      this.live = n.scene;
      this.next = null;
      progress = -1;
    }
    if (progress < 0) {
      this.writeLfos(this.live.scene.lfos);
      this.live.render(this.fc, this.sources.values, final);
      return;
    }
    const a = this.pool.acquire(final.width, final.height);
    const b = this.pool.acquire(final.width, final.height);
    this.writeLfos(this.live.scene.lfos);
    this.live.render(this.fc, this.sources.values, a);
    this.writeLfos(n!.scene.scene.lfos);
    n!.scene.morphFrom = n!.spec.type === 'morph' ? { scene: this.live, p: progress } : null;
    n!.scene.render(this.fc, this.sources.values, b);
    n!.scene.morphFrom = null;
    let type = n!.spec.type;
    const particles = type === 'morph' && this.morphKind(n!.scene) === 'particles';
    if (type === 'morph' && !particles) type = 'crossfade';
    const p = this.programs.transition;
    gl.bindFramebuffer(gl.FRAMEBUFFER, final.fbo);
    gl.viewport(0, 0, final.width, final.height);
    gl.useProgram(p.program);
    bindTex(gl, p, 'uA', UNIT_INPUT, a.texture);
    bindTex(gl, p, 'uB', UNIT_INPUT + 1, b.texture);
    gl.uniform1f(uniform(gl, p, 'uP'), progress);
    gl.uniform1i(uniform(gl, p, 'uType'), Math.max(1, TRANSITION_TYPES.indexOf(type)));
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    unbindUnits(gl, UNIT_INPUT, UNIT_INPUT + 1);
    if (particles) this.morph.draw(n!, a, b, final, progress);
    this.pool.release(a);
    this.pool.release(b);
  }

  /**
   * GPU watchdog (docs/04 "Shader safety"): repeated >1 s stalls mean a layer is too heavy for
   * this GPU. Switch the newest layer off before the browser decides to block WebGL for the site.
   */
  private watchdog(rawDt: number, nowMs: number): void {
    if (!this.visible) return;
    if (rawDt > 1.0 && rawDt < 30) this.stalls++;
    else if (nowMs - this.lastWatchCheck > 5000) {
      this.stalls = 0;
      this.lastWatchCheck = nowMs;
    }
    if (this.stalls < 3) return;
    this.stalls = 0;
    const suspect = (this.next?.scene ?? this.live).newestLayer();
    if (suspect)
      this.host.post({ type: 'slowLayer', layerId: suspect.layerId, name: suspect.name });
  }

  /**
   * Melody: jump to a new note at once, glide within a note, hold the last note while unsure,
   * and record a short history for scrolling displays.
   */
  private updatePitch(frame: Float32Array, dt: number): void {
    const fc = this.fc;
    const p = frame[F.PITCH]!;
    const c = frame[F.PITCH_CONF]!;
    if (c > 0.2 && p > 0) {
      fc.pitch =
        fc.pitch <= 0 || Math.abs(p - fc.pitch) > 0.75
          ? p
          : fc.pitch + (p - fc.pitch) * (1 - Math.exp(-dt / 0.04));
    }
    fc.pitchConf += (c - fc.pitchConf) * (1 - Math.exp(-dt / (c > fc.pitchConf ? 0.03 : 0.25)));
    for (let i = 0; i < 12; i++) fc.chroma[i] = frame[F.CHROMA + i]!;
    this.pitchClock += dt;
    if (this.pitchClock >= PITCH_HISTORY_DT) {
      this.pitchClock %= PITCH_HISTORY_DT;
      const h = fc.pitchHist;
      h.copyWithin(2, 0, h.length - 2);
      h[0] = fc.pitch;
      h[1] = c > 0.2 ? fc.pitchConf : 0;
    }
  }

  private follow(cur: number, raw: number): number {
    return cur + (raw - cur) * (raw > cur ? this.atk : this.rel);
  }

  // ---- outputs (multi-window / multi-monitor fan-out)

  private addOutput(
    id: string,
    canvas: OffscreenCanvas | HTMLCanvasElement,
    width: number,
    height: number,
    config: OutputConfig,
  ): void {
    canvas.width = Math.max(1, Math.round(width));
    canvas.height = Math.max(1, Math.round(height));
    const ctx = canvas.getContext('2d', { alpha: false, desynchronized: true }) as
      OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D | null;
    if (!ctx) return;
    this.outputs.set(id, { canvas, ctx, config });
  }

  /** Copy the presented frame (or a tile of it) to each output window. */
  private presentOutputs(): void {
    if (this.outputs.size === 0) return;
    const src = this.canvas as CanvasImageSource;
    for (const o of this.outputs.values()) {
      const { ctx, canvas, config } = o;
      const cw = canvas.width;
      const ch = canvas.height;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      if (config.pattern === 'test') {
        drawTestPattern(ctx, cw, ch, config.label);
        continue;
      }
      const r = config.region;
      const sx = r.x * this.width;
      const sy = r.y * this.height;
      const sw = Math.max(1, r.w * this.width);
      const sh = Math.max(1, r.h * this.height);
      const rot = config.rotation;
      ctx.translate(cw / 2, ch / 2);
      ctx.rotate((rot * Math.PI) / 180);
      const dw = rot % 180 === 0 ? cw : ch;
      const dh = rot % 180 === 0 ? ch : cw;
      ctx.globalAlpha = 1;
      ctx.drawImage(src, sx, sy, sw, sh, -dw / 2, -dh / 2, dw, dh);
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      if (config.brightness < 0.999) {
        ctx.globalAlpha = 1 - Math.max(0, config.brightness);
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, cw, ch);
        ctx.globalAlpha = 1;
      }
      if (config.pattern === 'identify') drawIdentify(ctx, cw, ch, config.label);
    }
  }

  // ---- thumbnails

  private thumbnail(id: number, tw: number, th: number): void {
    if (!this.final) return;
    // In preview mode, saving and favoriting refer to what's being prepared.
    const src = this.preview && this.previewTarget ? this.previewTarget : this.final;
    const gl = this.gl;
    const t = createRenderTarget(gl, tw, th);
    gl.bindFramebuffer(gl.FRAMEBUFFER, t.fbo);
    gl.viewport(0, 0, tw, th);
    gl.bindVertexArray(this.fc.vao);
    const p = this.programs.blit;
    gl.useProgram(p.program);
    bindTex(gl, p, 'uTex', UNIT_INPUT, src.texture);
    gl.uniform1f(uniform(gl, p, 'uGain'), 1);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    unbindUnits(gl, UNIT_INPUT, UNIT_INPUT);
    const pixels = new Uint8Array(tw * th * 4);
    gl.readPixels(0, 0, tw, th, gl.RGBA, gl.UNSIGNED_BYTE, pixels); // rare, user-triggered: sync is fine
    deleteRenderTarget(gl, t);
    this.host.post({ type: 'thumbnail', id, width: tw, height: th, pixels }, [pixels.buffer]);
  }

  // ---- stats

  private recordStats(nowMs: number, cpuMs: number): void {
    this.framesSinceStats++;
    this.totalFrames++;
    this.cpuAccum += cpuMs;
    if (!this.lastStats) this.lastStats = nowMs;
    const elapsed = nowMs - this.lastStats;
    if (elapsed < STATS_INTERVAL_MS) return;
    const n = this.next;
    const stats: RenderStats = {
      mode: this.mode,
      fps: (this.framesSinceStats * 1000) / elapsed,
      cpuMs: this.cpuAccum / this.framesSinceStats,
      scale: this.governor.scale,
      tier: this.governor.tier,
      refreshHz: this.governor.refreshHz,
      targetFps: this.governor.targetHz,
      frames: this.totalFrames,
      predictMs: Number.isFinite(this.predictMs) ? this.predictMs : 0,
      level: this.fc.level,
      governor: this.governor.status,
      luminance: this.limiter.luminance,
      flashGain: this.flashGain,
      poolMB: this.pool.bytes / 1e6,
      layers: this.live.scene.layers.filter((l) => l.enabled).length,
      modulations: this.live.compiled.modulationCount,
      transition: !n ? 'none' : n.start === null || !n.scene.ready ? 'waiting' : 'running',
      outputs: this.outputs.size,
      previewMix: this.preview ? this.previewMix : -1,
      takes: this.takes.length,
      liveVizId: this.live.scene.layers[0]?.vizId ?? '',
    };
    this.host.post({ type: 'stats', stats });
    this.framesSinceStats = 0;
    this.cpuAccum = 0;
    this.lastStats = nowMs;
  }
}

const lfoRandom = (cycle: number) => hash01(cycle * 7919);

type Ctx2D = OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D;

function drawIdentify(ctx: Ctx2D, w: number, h: number, label: string): void {
  ctx.fillStyle = 'rgba(0,0,0,0.55)';
  ctx.fillRect(0, 0, w, h);
  ctx.fillStyle = '#fff';
  ctx.font = `bold ${Math.round(h * 0.4)}px system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, w / 2, h / 2);
}

function drawTestPattern(ctx: Ctx2D, w: number, h: number, label: string): void {
  const colors = ['#fff', '#ff0', '#0ff', '#0f0', '#f0f', '#f00', '#00f', '#000'];
  colors.forEach((c, i) => {
    ctx.fillStyle = c;
    ctx.fillRect((i * w) / 8, 0, w / 8 + 1, h);
  });
  ctx.strokeStyle = 'rgba(255,255,255,0.9)';
  ctx.lineWidth = Math.max(1, Math.round(h / 540));
  const step = Math.round(h / 12);
  ctx.beginPath();
  for (let x = 0; x <= w; x += step) {
    ctx.moveTo(x + 0.5, 0);
    ctx.lineTo(x + 0.5, h);
  }
  for (let y = 0; y <= h; y += step) {
    ctx.moveTo(0, y + 0.5);
    ctx.lineTo(w, y + 0.5);
  }
  ctx.stroke();
  ctx.strokeRect(1, 1, w - 2, h - 2);
  ctx.beginPath();
  ctx.arc(w / 2, h / 2, h * 0.3, 0, Math.PI * 2);
  ctx.stroke();
  ctx.fillStyle = '#000';
  ctx.fillRect(w / 2 - h * 0.2, h / 2 - h * 0.06, h * 0.4, h * 0.12);
  ctx.fillStyle = '#fff';
  ctx.font = `bold ${Math.round(h * 0.07)}px system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(`${label} · ${w}×${h}`, w / 2, h / 2);
}
